import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { isEqual, isNil, isNull, omitBy } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';

import { FieldInfoModel } from '@app/services/summary.service';
import { InspectionsSummaryGroupType } from '@main-application/inspections/services/inspection.service';
import {
  FilterByFieldInfoModel,
  PeriodType,
  SummaryReportSettingsModel,
} from '@main-application/shared/services/summary-report-settings.service';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { filterNullish$ } from '@shared/utils/rxjs/filter-nullish.rxjs.util';
import { IInspectionFilterForm } from '@template/interfaces/filter.interface';
import { SummaryStatsV2Filter } from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';

import { InspectionDateFiltersService } from './inspection-date-filters.service';
import { InspectionViewType } from './inspections-view.service';
import { InspectionHasIssues, InspectionQueryType, InspectionsListConstants } from '../../inspections-list/constants';

export interface InspectionsFilter extends SummaryStatsV2Filter {
  builtIn: InspectionsSummaryGroupType;
  values: string;
  labels?: string;
}

export interface BaseSearchListRequest {
  sortBy: Array<{ sortBy: number; isDesc: boolean }>;
  page: number;
  pageSize: number;
  doNotReturnContent: boolean;
  startDate: string;
  endDate: string;
  queryType: number;
  modifiedSince?: string;
  periodDateSearchFields: number;
  searchText?: string;
}

export interface InspectionSearchRequest extends BaseSearchListRequest {
  inspectionByStatusFilter?: number;
  periodDateSearchFields: number;
  filters: InspectionsFilter[];
  isResident?: boolean;
  _periodType?: PeriodType;
}

export const DEFAULT_INSPECTIONS_PAGE_SIZE = 500;
export const DYNAMIC_FILTER_PRESET_ID = -100;

@Injectable({
  providedIn: 'root',
})
export class InspectionFiltersService {
  readonly defaultStandardFilters: Partial<IInspectionFilterForm> = {
    unitOrAssignee: null,
    status: null,
    inspectionByStatusFilter: null,
    assigneeId: null,
    templateIds: null,
    recurrencePeriod: null,
    timeframe: InspectionsListConstants.TimeFrame.ThisMonth,
    queryType: null,
    parentInspectionId: null,
    dynamicInspectionId: null,
    startDate: null,
    endDate: null,
    periodDateSearchFields: InspectionsListConstants.TimeWindow.DueDateOnly,
    categoryIds: null,
    hasIssues: null,
  };

  readonly defaultBulkFilters: Partial<IInspectionFilterForm> = {
    unitOrAssignee: null,
    assigneeId: null,
    templateIds: null,
    status: null,
    queryType: InspectionQueryType.Active,
    startDate: null,
    endDate: null,
    timeframe: InspectionsListConstants.TimeFrame.ThisMonth,
    periodDateSearchFields: InspectionsListConstants.TimeWindow.DueDateOnly,
  };

  readonly defaultInReviewFilters: Partial<IInspectionFilterForm> = {
    unitOrAssignee: null,
    assigneeId: null,
    templateIds: null,
    parentInspectionId: null,
    dynamicInspectionId: null,
    queryType: InspectionQueryType.Active,
  };

  readonly defaultDynamicFilters = {
    unitOrAssignee: null,
    assigneeId: null,
    templateIds: null,
    queryType: InspectionQueryType.Active,
  };

  readonly defaultRepeatingFilters: Partial<IInspectionFilterForm> = {
    unitOrAssignee: null,
    assigneeId: null,
    templateIds: null,
    recurrencePeriod: null,
    queryType: InspectionQueryType.Active,
  };

  readonly showableInspectionFilters = [
    InspectionsSummaryGroupType.Template,
    InspectionsSummaryGroupType.Assignee,
    InspectionsSummaryGroupType.Category,
    // InspectionsSummaryGroupType.Inspection,
    InspectionsSummaryGroupType.HasIssues,
    InspectionsSummaryGroupType.Status,
    InspectionsSummaryGroupType.DynamicInspectionId,
    InspectionsSummaryGroupType.IsArchived,
  ];

  readonly filterQueryParams = [
    'assigneeId',
    'status',
    'templateIds',
    'hasIssues',
    'categoryIds',
    'queryType',
    'dynamicInspectionId',
  ];
  readonly dateFilterQueryParams = ['startDate', 'endDate', 'timeframe', 'periodType', 'periodDateSearchFields'];
  readonly sortQueryParams = ['sortBy', 'isDescending'];

  #reloadFiltersSubject$ = new Subject<number>();
  reloadFiltersSubject$ = this.#reloadFiltersSubject$.asObservable();

  #filterControl$ = new BehaviorSubject<FormControl<FilterByFieldInfoModel[] | null>>(null);
  filterControl$ = this.#filterControl$.pipe(filterNullish$());

  constructor(
    private dateFilters: InspectionDateFiltersService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  setFilterControl(filterControl: FormControl<FilterByFieldInfoModel[] | null>) {
    this.#filterControl$.next(filterControl);
  }

  clearFilter() {
    this.#filterControl$.value?.setValue([]);
  }

  private getDefaultFilters(viewType: InspectionViewType) {
    switch (viewType) {
      case InspectionViewType.STANDARD_LIST:
      case InspectionViewType.RESIDENT_LIST:
        return { ...this.defaultStandardFilters };
      case InspectionViewType.BULK:
        return { ...this.defaultBulkFilters };
      case InspectionViewType.IN_REVIEW:
        return { ...this.defaultInReviewFilters };
      case InspectionViewType.DYNAMIC:
        return { ...this.defaultDynamicFilters };
      case InspectionViewType.REPEATING:
        return { ...this.defaultRepeatingFilters };
      default:
        return { ...this.defaultStandardFilters };
    }
  }

  private getStorageKey(viewType: InspectionViewType): string {
    const InspectionTabStorageKeys = {
      [InspectionViewType.STANDARD_LIST]: LocalStorageDataEnum.INSPECTION_LIST_USER_FILTER,
      [InspectionViewType.DYNAMIC]: LocalStorageDataEnum.INSPECTION_LIST_DYNAMIC_FILTER,
      [InspectionViewType.REPEATING]: LocalStorageDataEnum.INSPECTION_LIST_REPEATING_FILTER,
      [InspectionViewType.RESIDENT_LIST]: LocalStorageDataEnum.INSPECTION_LIST_RESIDENT_FILTER,
      [InspectionViewType.IN_REVIEW]: LocalStorageDataEnum.INSPECTION_LIST_IN_REVIEW_FILTER,
      [InspectionViewType.BULK]: LocalStorageDataEnum.INSPECTION_LIST_BULK_FILTER,
    };
    return InspectionTabStorageKeys[viewType] || LocalStorageDataEnum.INSPECTION_LIST_USER_FILTER;
  }

  getFiltersFromStorage(viewType: InspectionViewType): Partial<IInspectionFilterForm> {
    // const storageKey = this.getStorageKey(viewType);
    // try {
    //   const stored = localStorage.getItem(storageKey);
    //   if (stored) {
    //     return JSON.parse(stored);
    //   }
    // } catch (error) {
    //   console.warn('Failed to parse filters from localStorage', error);
    // }

    // Return only default filters
    return this.getDefaultFilters(viewType);
  }

  convertOldFiltersToNewFormat(oldFilters: Partial<IInspectionFilterForm>): InspectionsFilter[] {
    const filters: InspectionsFilter[] = [];

    const fieldMappings: Array<{
      oldField: keyof IInspectionFilterForm;
      builtIn: InspectionsSummaryGroupType;
    }> = [
      { oldField: 'templateIds', builtIn: InspectionsSummaryGroupType.Template },
      { oldField: 'assigneeId', builtIn: InspectionsSummaryGroupType.Assignee },
      { oldField: 'categoryIds', builtIn: InspectionsSummaryGroupType.Category },
      { oldField: 'hasIssues', builtIn: InspectionsSummaryGroupType.HasIssues },
      { oldField: 'status', builtIn: InspectionsSummaryGroupType.Status },
      { oldField: 'dynamicInspectionId', builtIn: InspectionsSummaryGroupType.DynamicInspectionId },
      { oldField: 'queryType', builtIn: InspectionsSummaryGroupType.IsArchived },
    ];

    fieldMappings.forEach(mapping => {
      const value = oldFilters[mapping.oldField];
      if (value !== null && value !== '') {
        const valuesString = this.convertValueToString(value);
        if (valuesString) {
          filters.push({
            builtIn: mapping.builtIn,
            values: valuesString,
          });
        }
      } else if (value === '') {
        filters.push({
          builtIn: mapping.builtIn,
          values: '',
        });
      }
    });

    return filters;
  }

  getFilterNameByBuiltIn(builtIn: InspectionsSummaryGroupType): string {
    switch (builtIn) {
      case InspectionsSummaryGroupType.Assignee:
        return 'assigneeId';
      case InspectionsSummaryGroupType.Category:
        return 'categoryIds';
      case InspectionsSummaryGroupType.PropertyGroup:
        return 'collectionIds';
      case InspectionsSummaryGroupType.HasIssues:
        return 'hasIssues';
      case InspectionsSummaryGroupType.Property:
        return 'propertyIds';
      case InspectionsSummaryGroupType.Status:
        return 'status';
      case InspectionsSummaryGroupType.Template:
        return 'templateIds';
      case InspectionsSummaryGroupType.DynamicInspectionId:
        return 'dynamicInspectionId';
      case InspectionsSummaryGroupType.IsArchived:
        return 'queryType';
      default:
        return '';
    }
  }

  private convertValueToString(value: any): string {
    if (isNil(value)) return '';

    if (Array.isArray(value)) {
      return value.join(',');
    }

    if (typeof value === 'string') {
      return value;
    }

    return value.toString();
  }

  getBuiltInLabel(builtIn: InspectionsSummaryGroupType): string {
    const labels = {
      [InspectionsSummaryGroupType.Assignee]: 'Assignee',
      [InspectionsSummaryGroupType.Category]: 'Category',
      [InspectionsSummaryGroupType.DynamicInspectionId]: 'Dynamic',
      [InspectionsSummaryGroupType.HasIssues]: 'Has Issues',
      [InspectionsSummaryGroupType.IsArchived]: 'Is Archived',
      [InspectionsSummaryGroupType.PropertyGroup]: 'Collection',
      [InspectionsSummaryGroupType.Property]: 'Property',
      [InspectionsSummaryGroupType.Status]: 'Status',
      [InspectionsSummaryGroupType.Template]: 'Template',
      [InspectionsSummaryGroupType.UnitType]: 'Unit Type',
    };

    return labels[builtIn] || 'Unknown Filter';
  }

  getFilterOptions(): FilterByFieldInfoModel[] {
    return Object.entries(InspectionsSummaryGroupType)
      .filter(([key, value]) => this.showableInspectionFilters.includes(+value))
      .map(([key, value]) => ({
        fieldType: value as number,
        label: this.getBuiltInLabel(value as InspectionsSummaryGroupType),
      }));
  }

  getFilterFromFieldModel(fieldModels: FieldInfoModel[]): InspectionsFilter[] {
    if (!fieldModels) return [];

    return fieldModels.map(fieldModel => ({
      builtIn: fieldModel.fieldType,
      customFieldId: fieldModel.customFieldId,
      customFieldValueModifier: fieldModel.customFieldModifier,
      values: fieldModel.values,
      labels: fieldModel.labels,
    }));
  }

  convertStringFiltersToNumbers(ids: string): number[] | string | null {
    if (ids === '') return '';
    const result = ids
      .split(',')
      .map(id => Number(id))
      .filter(id => !isNaN(id));
    return result.length > 0 ? result : null;
  }

  readonly sortMap: { [key: string]: number } = {
    'unit.name': 0,
    'unit.propertyName': 1,
    dueDate: 3,
    status: 5,
    expiredAt: 6,
    completedAt: 7,
    inspectionType: 8,
    items: 9,
  };

  getSortByEnum(columnKey: string): number {
    return this.sortMap[columnKey] ?? this.sortMap[3];
  }

  getSortName(columnId: number): string {
    return Object.keys(this.sortMap).find(key => this.sortMap[key] === columnId) || '';
  }

  getFiltersFromQueryParams(currentSettings: SummaryReportSettingsModel): InspectionsFilter[] | null {
    const queryParams = { ...this.route.snapshot.queryParams };
    const oldFiltersFormat: Partial<IInspectionFilterForm> = omitBy(
      {
        assigneeId: this.queryNotEqualSettings(
          InspectionsSummaryGroupType.Assignee,
          queryParams.assigneeId,
          currentSettings
        )
          ? this.convertStringFiltersToNumbers(queryParams.assigneeId)
          : null,
        status: this.queryNotEqualSettings(InspectionsSummaryGroupType.Status, queryParams.status, currentSettings)
          ? this.convertStringFiltersToNumbers(queryParams.status)
          : null,
        templateIds: this.queryNotEqualSettings(
          InspectionsSummaryGroupType.Template,
          queryParams.templateIds,
          currentSettings
        )
          ? this.convertStringFiltersToNumbers(queryParams.templateIds)
          : null,
        categoryIds: this.queryNotEqualSettings(
          InspectionsSummaryGroupType.Category,
          queryParams.categoryIds,
          currentSettings
        )
          ? this.convertStringFiltersToNumbers(queryParams.categoryIds)
          : null,
        dynamicInspectionId: this.queryNotEqualSettings(
          InspectionsSummaryGroupType.DynamicInspectionId,
          queryParams.dynamicInspectionId,
          currentSettings
        )
          ? this.convertStringFiltersToNumbers(queryParams.dynamicInspectionId)
          : null,
        hasIssues:
          Number(queryParams.hasIssues) !== InspectionHasIssues.Any &&
          this.queryNotEqualSettings(InspectionsSummaryGroupType.HasIssues, queryParams.hasIssues, currentSettings)
            ? queryParams.hasIssues !== ''
              ? this.convertStringFiltersToNumbers(queryParams.hasIssues)
              : ''
            : null,
        queryType: this.queryNotEqualSettings(
          InspectionsSummaryGroupType.IsArchived,
          queryParams.queryType,
          currentSettings
        )
          ? this.convertStringFiltersToNumbers(queryParams.queryType)
          : null,
      },
      isNull
    );
    if (Object.keys(oldFiltersFormat).length) {
      const removingQueryParams = this.filterQueryParams.filter(param => {
        return oldFiltersFormat[param] === null;
      });
      this.removeQueryParams(removingQueryParams);
      const filters = this.convertOldFiltersToNewFormat(oldFiltersFormat);
      return filters;
    }
    return null;
  }

  getSortingFromQueryParams(): { sortBy: number; isDesc: boolean } | null {
    const queryParams = { ...this.route.snapshot.queryParams };
    if (queryParams.sortBy && queryParams.isDescending) {
      const sortColumnName = queryParams.sortBy;
      const isDesc = queryParams.isDescending === 'true';

      const sortBy = this.getSortByEnum(sortColumnName);
      return sortBy >= 0 ? { sortBy, isDesc } : null;
    }
    return null;
  }

  private queryNotEqualSettings(
    builtInId: number,
    values: string | number,
    currentSettings: SummaryReportSettingsModel
  ): boolean {
    if (isNil(values)) return false;
    const settingsFilter = currentSettings.filterBy
      ?.find(f => f.fieldType === builtInId)
      ?.values?.split(',')
      .sort();
    const valuesArray = values?.toString().split(',').sort();
    if (!settingsFilter && !valuesArray) {
      return false;
    }
    return !isEqual(settingsFilter, valuesArray);
  }

  removeQueryParams(removedParams: string[]) {
    const queryParams = { ...this.route.snapshot.queryParams };
    let changed = false;
    removedParams.forEach(param => {
      if (queryParams.hasOwnProperty(param)) {
        changed = true;
        delete queryParams[param];
      }
    });
    if (!changed) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
    });
  }

  addQueryParams(params: Params) {
    const queryParams = { ...this.route.snapshot.queryParams, ...params };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
    });
  }

  reloadFilters() {
    this.#reloadFiltersSubject$.next(new Date().getTime());
  }
}
